/**
 * Sentry Performance Monitoring Configuration
 * This file defines performance thresholds and monitoring rules for GLYYD
 */

import * as Sentry from '@sentry/react';
import { DEBUG } from './config/debug';

// Performance thresholds for different operation types
export const PERFORMANCE_THRESHOLDS = {
  // File upload operations
  fileUpload: {
    small: { size: 1024 * 1024, threshold: 2000 }, // < 1MB should complete in 2s
    medium: { size: 10 * 1024 * 1024, threshold: 5000 }, // < 10MB in 5s
    large: { size: 100 * 1024 * 1024, threshold: 15000 }, // < 100MB in 15s
  },

  // Chart rendering operations
  chartRender: {
    simple: { dataPoints: 1000, threshold: 500 }, // < 1k points in 500ms
    medium: { dataPoints: 10000, threshold: 2000 }, // < 10k points in 2s
    complex: { dataPoints: 100000, threshold: 5000 }, // < 100k points in 5s
  },

  // API operations
  api: {
    read: 1000, // GET requests should complete in 1s
    write: 2000, // POST/PUT requests in 2s
    delete: 1000, // DELETE requests in 1s
    batch: 5000, // Batch operations in 5s
  },

  // Page load targets
  pageLoad: {
    initial: 3000, // Initial page load in 3s
    subsequent: 1000, // Subsequent navigation in 1s
  },

  // Database operations
  database: {
    query: 100, // Simple queries in 100ms
    mutation: 200, // Mutations in 200ms
    transaction: 500, // Transactions in 500ms
  },
};

/**
 * Configure performance monitoring for a specific operation
 */
export function monitorPerformance<T>(
  operationType: string,
  operationName: string,
  callback: (span: Sentry.Span) => T,
  metadata?: Record<string, any>
): T {
  if (DEBUG.ALL) {
    // Lightweight guard to avoid noisy logs on staging/production
    // eslint-disable-next-line no-console
    console.log(
      '🔍 MONITOR_PERFORMANCE DEBUG: Starting',
      operationName,
      'with metadata:',
      metadata
    );
  }

  return Sentry.startSpan(
    {
      op: operationType,
      name: operationName,
      attributes: metadata,
    },
    (span) => {
      if (DEBUG.ALL) {
        // eslint-disable-next-line no-console
        console.log(
          '🔍 MONITOR_PERFORMANCE DEBUG: Span created for',
          operationName,
          'span exists:',
          !!span
        );
      }
      return callback(span);
    }
  );
}

/**
 * Check if an operation exceeded its performance threshold
 */
export function checkPerformanceThreshold(
  duration: number,
  operationType: string,
  metadata?: Record<string, any>
): boolean {
  let threshold: number;

  switch (operationType) {
    case 'file.upload':
      const fileSize = metadata?.fileSize || 0;
      if (fileSize < PERFORMANCE_THRESHOLDS.fileUpload.small.size) {
        threshold = PERFORMANCE_THRESHOLDS.fileUpload.small.threshold;
      } else if (fileSize < PERFORMANCE_THRESHOLDS.fileUpload.medium.size) {
        threshold = PERFORMANCE_THRESHOLDS.fileUpload.medium.threshold;
      } else {
        threshold = PERFORMANCE_THRESHOLDS.fileUpload.large.threshold;
      }
      break;

    case 'ui.render.chart':
      const dataPoints = metadata?.dataPoints || 0;
      if (dataPoints < PERFORMANCE_THRESHOLDS.chartRender.simple.dataPoints) {
        threshold = PERFORMANCE_THRESHOLDS.chartRender.simple.threshold;
      } else if (dataPoints < PERFORMANCE_THRESHOLDS.chartRender.medium.dataPoints) {
        threshold = PERFORMANCE_THRESHOLDS.chartRender.medium.threshold;
      } else {
        threshold = PERFORMANCE_THRESHOLDS.chartRender.complex.threshold;
      }
      break;

    case 'http.client':
      const method = metadata?.method || 'GET';
      if (method === 'GET') {
        threshold = PERFORMANCE_THRESHOLDS.api.read;
      } else if (['POST', 'PUT', 'PATCH'].includes(method)) {
        threshold = PERFORMANCE_THRESHOLDS.api.write;
      } else if (method === 'DELETE') {
        threshold = PERFORMANCE_THRESHOLDS.api.delete;
      } else {
        threshold = PERFORMANCE_THRESHOLDS.api.batch;
      }
      break;

    case 'pageload':
      threshold = metadata?.isInitial
        ? PERFORMANCE_THRESHOLDS.pageLoad.initial
        : PERFORMANCE_THRESHOLDS.pageLoad.subsequent;
      break;

    case 'db.query':
      threshold = PERFORMANCE_THRESHOLDS.database.query;
      break;

    case 'db.mutation':
      threshold = PERFORMANCE_THRESHOLDS.database.mutation;
      break;

    case 'db.transaction':
      threshold = PERFORMANCE_THRESHOLDS.database.transaction;
      break;

    default:
      threshold = 5000; // Default 5s threshold
  }

  return duration > threshold;
}

/**
 * Report a slow operation to Sentry
 */
export function reportSlowOperation(
  operationType: string,
  operationName: string,
  duration: number,
  metadata?: Record<string, any>
): void {
  const threshold = getThresholdForOperation(operationType, metadata);

  if (duration > threshold) {
    // Create a custom performance issue
    Sentry.captureMessage(`Slow operation detected: ${operationName}`, {
      level: 'warning',
      tags: {
        operation_type: operationType,
        operation_name: operationName,
        performance_issue: true,
      },
      extra: {
        duration,
        threshold,
        exceeded_by: duration - threshold,
        metadata,
      },
    });

    // Also add as breadcrumb for tracking
    Sentry.addBreadcrumb({
      category: 'performance',
      message: 'Slow operation detected',
      level: 'warning',
      data: {
        operation_type: operationType,
        operation_name: operationName,
        duration,
        threshold,
        severity: getSeverity(duration, threshold),
      },
    });
  }
}

/**
 * Get severity level based on how much threshold was exceeded
 */
function getSeverity(duration: number, threshold: number): string {
  const ratio = duration / threshold;
  if (ratio > 3) return 'critical';
  if (ratio > 2) return 'high';
  if (ratio > 1.5) return 'medium';
  return 'low';
}

/**
 * Get threshold for a specific operation
 */
function getThresholdForOperation(operationType: string, metadata?: Record<string, any>): number {
  // Implementation similar to checkPerformanceThreshold
  // but returns the threshold value instead of boolean
  let threshold: number;

  switch (operationType) {
    case 'file.upload':
      const fileSize = metadata?.fileSize || 0;
      if (fileSize < PERFORMANCE_THRESHOLDS.fileUpload.small.size) {
        threshold = PERFORMANCE_THRESHOLDS.fileUpload.small.threshold;
      } else if (fileSize < PERFORMANCE_THRESHOLDS.fileUpload.medium.size) {
        threshold = PERFORMANCE_THRESHOLDS.fileUpload.medium.threshold;
      } else {
        threshold = PERFORMANCE_THRESHOLDS.fileUpload.large.threshold;
      }
      break;

    case 'ui.render.chart':
      const dataPoints = metadata?.dataPoints || 0;
      if (dataPoints < PERFORMANCE_THRESHOLDS.chartRender.simple.dataPoints) {
        threshold = PERFORMANCE_THRESHOLDS.chartRender.simple.threshold;
      } else if (dataPoints < PERFORMANCE_THRESHOLDS.chartRender.medium.dataPoints) {
        threshold = PERFORMANCE_THRESHOLDS.chartRender.medium.threshold;
      } else {
        threshold = PERFORMANCE_THRESHOLDS.chartRender.complex.threshold;
      }
      break;

    default:
      threshold = 5000; // Default 5s threshold
  }

  return threshold;
}

/**
 * Web Vitals monitoring configuration
 */
export function setupWebVitalsMonitoring(): void {
  // Check if PerformanceObserver is available
  if (typeof PerformanceObserver === 'undefined') {
    return;
  }

  try {
    // Largest Contentful Paint (LCP)
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'largest-contentful-paint') {
          const lcpValue = entry.startTime;
          Sentry.addBreadcrumb({
            category: 'web_vitals',
            message: 'LCP measurement',
            data: {
              value: lcpValue,
              rating: getLCPRating(lcpValue),
            },
          });
        }
      }
    }).observe({ entryTypes: ['largest-contentful-paint'], buffered: true });
  } catch (e) {
    // LCP monitoring not supported
  }

  try {
    // First Input Delay (FID)
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'first-input') {
          const fidEntry = entry as PerformanceEntry & { processingStart: number };
          const fidValue = fidEntry.processingStart - entry.startTime;
          Sentry.addBreadcrumb({
            category: 'web_vitals',
            message: 'FID measurement',
            data: {
              value: fidValue,
              rating: getFIDRating(fidValue),
            },
          });
        }
      }
    }).observe({ entryTypes: ['first-input'], buffered: true });
  } catch (e) {
    // FID monitoring not supported
  }

  try {
    // Cumulative Layout Shift (CLS)
    let clsValue = 0;
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'layout-shift' && !(entry as any).hadRecentInput) {
          clsValue += (entry as any).value;
          Sentry.addBreadcrumb({
            category: 'web_vitals',
            message: 'CLS measurement',
            data: {
              value: clsValue,
              rating: getCLSRating(clsValue),
            },
          });
        }
      }
    }).observe({ entryTypes: ['layout-shift'], buffered: true });
  } catch (e) {
    // CLS monitoring not supported
  }
}

// Web Vitals rating functions
function getLCPRating(value: number): string {
  if (value <= 2500) return 'good';
  if (value <= 4000) return 'needs-improvement';
  return 'poor';
}

function getFIDRating(value: number): string {
  if (value <= 100) return 'good';
  if (value <= 300) return 'needs-improvement';
  return 'poor';
}

function getCLSRating(value: number): string {
  if (value <= 0.1) return 'good';
  if (value <= 0.25) return 'needs-improvement';
  return 'poor';
}

/**
 * Initialize performance monitoring
 */
export function initializePerformanceMonitoring(): void {
  // Disable performance monitoring in development
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log('[PERFORMANCE_DEBUG] Performance monitoring disabled in development environment');
    return;
  }

  // Set up Web Vitals monitoring
  setupWebVitalsMonitoring();

  // Note: Transaction sampling is configured in main Sentry initialization

  // Add global performance monitoring
  if (
    typeof window !== 'undefined' &&
    window.performance &&
    typeof PerformanceObserver !== 'undefined'
  ) {
    try {
      // Monitor long tasks
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 50) {
            // Tasks longer than 50ms
            Sentry.addBreadcrumb({
              category: 'performance',
              message: 'Long task detected',
              level: 'warning',
              data: {
                duration: entry.duration,
                startTime: entry.startTime,
              },
            });
          }
        }
      });

      observer.observe({ entryTypes: ['longtask'] });
    } catch (e) {
      // Long task monitoring not supported
    }
  }

  // Performance monitoring initialized
}
